/**
 * The phone calendar's Month and List — Aaron, Oct 25, phone notes 3.2 and
 * 3.3 (`docs/activity.md` §10.2), after Outlook's phone calendar: a month
 * after month you scroll through, each day's things as single coloured lines,
 * and one continuous agenda with a heading for every day.
 *
 * What a day holds, the words over it and how many lines fit a cell, kept
 * here so the two views and their tests read one answer.
 */
import { addDays, addMonths, parseDay } from '../calendar.js';
import { monthWeeks } from './week.js';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
/** `2026-10` — a month, as the views page and cache by it. */
export const monthKeyOf = (date) => date.slice(0, 7);
/** The month `n` after (or before) a month key. */
export const addMonthKey = (month, n) => monthKeyOf(addMonths(`${month}-01`, n));
/** The first and last day of a month key — one `GET /activity/calendar` each. */
export function monthSpan(month) {
    const from = `${month}-01`;
    return { from, to: addDays(addMonths(from, 1), -1) };
}
/** Every month key from `from` to `to`, both included. */
export function monthKeysBetween(from, to) {
    const out = [];
    for (let month = monthKeyOf(from); month <= monthKeyOf(to); month = addMonthKey(month, 1))
        out.push(month);
    return out;
}
/** *October 2026* — a month's heading in Month. */
export function monthTitle(month) {
    const { year, month: number } = parseDay(`${month}-01`);
    return `${MONTHS[number - 1]} ${year}`;
}
/**
 * A month as Sunday-first weeks of its own days, a day of another month left
 * empty (`null`) — so in a list of months every day is drawn once.
 */
export function monthCells(month) {
    return monthWeeks(`${month}-01`).map((week) => week.map((day) => (monthKeyOf(day) === month ? day : null)));
}
/**
 * The agenda's day heading (Outlook's): *Today · Thursday, October 22*,
 * *Tomorrow · Friday, October 23*, *Yesterday · …*, else the weekday and the
 * date — with the year when it is not this one (*Friday, January 1, 2027*).
 */
export function agendaHeading(date, today) {
    const { year, month, day } = parseDay(date);
    const sameYear = year === parseDay(today).year;
    const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay()];
    const words = `${weekday}, ${MONTHS[month - 1]} ${day}${sameYear ? '' : `, ${year}`}`;
    const lead = date === today ? 'Today' : date === addDays(today, 1) ? 'Tomorrow'
        : date === addDays(today, -1) ? 'Yesterday' : null;
    return { lead, words };
}
/** The heading as one line: *Today · Thursday, October 22*. */
export function agendaHeadingWords(date, today) {
    const { lead, words } = agendaHeading(date, today);
    return lead ? `${lead} · ${words}` : words;
}
/** Lines a Month cell holds (its 92 px under the date — Aaron, Oct 25, note 3.2). */
export const MONTH_CELL_LINES = 4;
/**
 * As many as fit, then *+n* (note 3.2): everything when it fits; otherwise
 * one line fewer, so the last line can say how many more there are.
 */
export function fitLines(count, room = MONTH_CELL_LINES) {
    if (count <= room)
        return { shown: count, more: 0 };
    const shown = Math.max(0, room - 1);
    return { shown, more: count - shown };
}
const isStopKind = (item) => item.kind === 'appointment' || item.kind === 'meeting';
/** Whether Show leaves an item on (§6.7). */
export function shownBy(show, item) {
    if (isStopKind(item))
        return show.visits;
    if (item.kind === 'task')
        return show.tasks;
    return show.blocks;
}
/**
 * Everything on one date for the people shown, each once — a meeting several
 * of them are going to is the first one's (§10.2: *List* with several is
 * Combined by nature) — in the order a day reads: all-day first, then by
 * start, a longer one first when two start together.
 */
export function entriesOn(answer, date, show) {
    if (!answer)
        return [];
    const seen = new Set();
    const out = [];
    for (const person of answer.people) {
        for (const item of person.days.find((d) => d.date === date)?.items ?? []) {
            if (seen.has(item.key) || !shownBy(show, item))
                continue;
            seen.add(item.key);
            out.push({ item, person });
        }
    }
    return out.sort((a, b) => Number(b.item.allDay) - Number(a.item.allDay)
        || a.item.start - b.item.start || b.item.end - a.item.end);
}
//# sourceMappingURL=agenda.js.map